/* eslint-disable react/no-unstable-nested-components */
import { createColumnHelper } from "@tanstack/react-table";
import CompanyCorporateActionsView from "components/company/v3/CompanyCorporateActionsView";
import classNames from "components/ui/classNames";
import getYear from "date-fns/getYear";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IStockDividendsCorporateAction } from "types/graphqlTypes";

interface AmericanStockDividendsContentProps {
  data: IStockDividendsCorporateAction[];
  isRenderPageDefault: boolean;
}

export default function AmericanStockDividendsContent({
  data,
  isRenderPageDefault,
}: AmericanStockDividendsContentProps) {
  const [filterStockDividendsByYear, setFilterStockDividendsByYear] = useState<number | null>(null);
  const intl = useIntl();

  const columnHelper = createColumnHelper<IStockDividendsCorporateAction>();

  const availableYears = useMemo(
    () => [
      ...new Set(
        data
          ?.map((dividend) => {
            if (dividend.excludingDate) {
              return getYear(parseDateYmd(dividend.excludingDate));
            }

            return undefined;
          })
          .filter(notUndefinedOrNull),
      ),
    ],
    [data],
  );

  const arrayOfDataFilteredByYear = useMemo(
    () =>
      data?.filter((node) => {
        if (!filterStockDividendsByYear) {
          return true;
        }

        return getYear(parseDateYmd(node.excludingDate)) === filterStockDividendsByYear;
      }),
    [data, filterStockDividendsByYear],
  );

  const columns = [
    columnHelper.accessor("excludingDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Data-Ex" id="tsg1zP" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{formatDateOrReturnHyphen(intl, info.getValue())}</span>
        </div>
      ),
    }),
    columnHelper.accessor("sharePaid", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Bonificação" id="UjC0PD" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{formatValue(info.getValue())}</span>
        </div>
      ),
    }),
  ];

  return (
    <CompanyCorporateActionsView<IStockDividendsCorporateAction>
      data={arrayOfDataFilteredByYear || []}
      columns={columns}
      availableYears={availableYears}
      filterByYear={filterStockDividendsByYear}
      setFilterByYear={setFilterStockDividendsByYear}
      isRenderPageDefault={isRenderPageDefault}
    />
  );
}

function formatValue(value?: number | null) {
  if (!value) {
    return "-";
  }

  return numeral(value).format("0.00").replace(".00", "").replace(".", ",");
}
